import React, { useState, useEffect, useCallback, useContext } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  MarkLineComponent,
  MarkPointComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import AppContext from 'context/Context';
import useToken from 'hooks/useToken';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faCalendarAlt,
  faChevronLeft,
  faChevronRight,
  faInbox,
  faHandsHelping,
  faCheckCircle,
  faPercent,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { Spinner, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import DatePicker, { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
import AgentFilterSelect from './AgentFilterSelect';

registerLocale('es', es);

echarts.use([
  LineChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  MarkLineComponent,
  MarkPointComponent,
  CanvasRenderer
]);

const formatLocalDateStr = dt => {
  if (!dt) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};


const BurnupTicketsChart = ({
  selectedAgentId = '',
  onSelectAgent,
  agents = [],
  currentUserId,
  currentUserName,
  isLoadingAgents = false
}) => {
  const { token, idUsuario, nombreCompleto } = useToken();
  const { config: { isDark } = {} } = useContext(AppContext);

  const effectiveUserId = currentUserId || idUsuario;
  const effectiveUserName = currentUserName || nombreCompleto;

  const today = new Date();
  const todayStr = formatLocalDateStr(today);
  const [selectedDateObj, setSelectedDateObj] = useState(today);
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 576 : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 576);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const selectedDate = formatLocalDateStr(selectedDateObj);

  const fetchBurnupData = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      const hasAgent = selectedAgentId && selectedAgentId !== 'todos' && selectedAgentId !== 'all';
      if (hasAgent) {
        params.set('idUsuarioAsignado', selectedAgentId);
      }
      if (selectedDate) {
        params.set('dFecha', selectedDate);
      }

      const resp = await fetch(`/api/SupportEngine/GetDashboardBurnupChart?${params.toString()}`, {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        }
      });

      if (resp.ok) {
        const json = await resp.json();
        const data = json.data || json || [];
        setChartData(Array.isArray(data) ? data : []);
      }
    } catch {
      // Silencioso
    } finally {
      setIsLoading(false);
    }
  }, [token, selectedAgentId, selectedDate]);

  useEffect(() => {
    fetchBurnupData();
  }, [fetchBurnupData]);

  // Manejo de cambio de día
  const handlePrevDay = () => {
    const prev = new Date(selectedDateObj);
    prev.setDate(prev.getDate() - 1);
    setSelectedDateObj(prev);
  };

  const handleNextDay = () => {
    const next = new Date(selectedDateObj);
    next.setDate(next.getDate() + 1);
    setSelectedDateObj(next);
  };

  const handleGoToday = () => {
    setSelectedDateObj(new Date());
  };

  const isToday = selectedDate === todayStr;
  const currentHour = today.getHours();
  const maxHour = isToday ? currentHour : 23;

  // Mapeo horario (0 a maxHour)
  const categories = [];
  const llegadaMap = {};
  const tomadoMap = {};
  const resueltoMap = {};
  const llegadaDeltaMap = {};
  const tomadoDeltaMap = {};
  const resueltoDeltaMap = {};

  for (let h = 0; h <= maxHour; h++) {
    const key = `${h.toString().padStart(2, '0')}:00`;
    categories.push(key);
    llegadaMap[h] = 0;
    tomadoMap[h] = 0;
    resueltoMap[h] = 0;
    llegadaDeltaMap[h] = 0;
    tomadoDeltaMap[h] = 0;
    resueltoDeltaMap[h] = 0;
  }

  chartData.forEach(item => {
    const h = item.iHora;
    if (h !== undefined && h !== null && h <= maxHour) {
      llegadaMap[h] = item.iLlegadosAcumulado ?? 0;
      tomadoMap[h] = item.iTomadosAcumulado ?? 0;
      resueltoMap[h] = item.iResueltosAcumulado ?? 0;
      llegadaDeltaMap[h] = item.iLlegadosHora ?? 0;
      tomadoDeltaMap[h] = item.iTomadosHora ?? 0;
      resueltoDeltaMap[h] = item.iResueltosHora ?? 0;
    }
  });

  const seriesLlegados = categories.map((_, idx) => llegadaMap[idx] || 0);
  const seriesTomados = categories.map((_, idx) => tomadoMap[idx] || 0);
  const seriesResueltos = categories.map((_, idx) => resueltoMap[idx] || 0);

  // Totales acumulados al corte de la última hora calculada
  const totalLlegados = seriesLlegados.length > 0 ? seriesLlegados[seriesLlegados.length - 1] : 0;
  const totalTomados = seriesTomados.length > 0 ? seriesTomados[seriesTomados.length - 1] : 0;
  const totalResueltos = seriesResueltos.length > 0 ? seriesResueltos[seriesResueltos.length - 1] : 0;
  const tasaResolucion = totalLlegados > 0 ? Math.round((totalResueltos / totalLlegados) * 100) : 0;

  // Configuración del gráfico ECharts
  const getOption = () => {
    const textColor = isDark ? '#94a3b8' : '#64748b';
    const splitLineColor = isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.05)';
    const axisLineColor = isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.1)';

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: isDark ? '#1e293b' : '#ffffff',
        borderColor: isDark ? '#334155' : '#e2e8f0',
        borderWidth: 1,
        textStyle: { color: isDark ? '#f8fafc' : '#1e293b', fontSize: 12 },
        formatter: params => {
          if (!params || params.length === 0) return '';
          const hourLabel = params[0].name;
          const hourNum = parseInt(hourLabel.split(':')[0], 10);

          const deltaLleg = llegadaDeltaMap[hourNum] || 0;
          const deltaTom = tomadoDeltaMap[hourNum] || 0;
          const deltaRes = resueltoDeltaMap[hourNum] || 0;

          let html = `<div style="font-weight: 700; margin-bottom: 6px; padding-bottom: 4px; border-bottom: 1px solid ${isDark ? '#334155' : '#e2e8f0'}">
            Hora: ${hourLabel}
          </div>`;

          params.forEach(item => {
            const isLleg = item.seriesName.includes('Llegando');
            const isTom = item.seriesName.includes('Tomando');
            const isRes = item.seriesName.includes('Resolviendo');

            let deltaText = '';
            if (isLleg) deltaText = ` (+${deltaLleg} en la hora)`;
            if (isTom) deltaText = ` (+${deltaTom} en la hora)`;
            if (isRes) deltaText = ` (+${deltaRes} en la hora)`;

            html += `
              <div style="display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-top: 3px;">
                <span>${item.marker} ${item.seriesName}:</span>
                <span style="font-weight: 700;">${item.value} <span style="font-size: 10px; opacity: 0.75;">${deltaText}</span></span>
              </div>
            `;
          });

          return html;
        }
      },
      legend: {
        data: ['Tickets que van llegando', 'Tickets tomados por agentes', 'Tickets resueltos'],
        top: 0,
        right: 10,
        textStyle: {
          color: textColor,
          fontSize: 12,
          fontWeight: 500
        },
        itemWidth: 14,
        itemHeight: 10
      },
      grid: {
        top: 36,
        left: '2%',
        right: '3%',
        bottom: isMobile ? '12%' : '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: categories,
        boundaryGap: false,
        axisLine: { lineStyle: { color: axisLineColor } },
        axisLabel: {
          color: textColor,
          fontSize: isMobile ? 10 : 11,
          rotate: isMobile ? 35 : 0,
          hideOverlap: true,
          interval: 'auto',
          margin: isMobile ? 8 : 10,
          formatter: value => {
            if (!value) return '';
            if (isMobile && value.endsWith(':00')) {
              const hourNum = parseInt(value.split(':')[0], 10);
              return `${hourNum}h`;
            }
            return value;
          }
        },
        axisTick: { alignWithLabel: true }
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        axisLine: { show: false },
        axisLabel: { color: textColor, fontSize: 11 },
        splitLine: { lineStyle: { color: splitLineColor, type: 'dashed' } }
      },
      series: [
        {
          name: 'Tickets que van llegando',
          type: 'line',
          data: seriesLlegados,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: { color: '#2563eb' },
          lineStyle: { width: 3, color: '#2563eb' },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(37, 99, 235, 0.20)' },
              { offset: 1, color: 'rgba(37, 99, 235, 0.01)' }
            ])
          }
        },
        {
          name: 'Tickets tomados por agentes',
          type: 'line',
          data: seriesTomados,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: { color: '#8b5cf6' },
          lineStyle: { width: 3, color: '#8b5cf6' },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(139, 92, 246, 0.20)' },
              { offset: 1, color: 'rgba(139, 92, 246, 0.01)' }
            ])
          }
        },
        {
          name: 'Tickets resueltos',
          type: 'line',
          data: seriesResueltos,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: { color: '#16a34a' },
          lineStyle: { width: 3, color: '#16a34a' },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(22, 163, 74, 0.20)' },
              { offset: 1, color: 'rgba(22, 163, 74, 0.01)' }
            ])
          }
        }
      ]
    };
  };

  return (
    <div className="card shadow-sm border-0 mb-3" style={{ borderRadius: '12px' }}>
      <div className="card-header bg-transparent border-0 pt-3 pb-2 px-3 px-md-4">
        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3">
          {/* Título, subtítulo y estadísticas compactas al lado */}
          <div className="d-flex flex-column flex-xl-row align-items-start align-items-xl-center gap-2 gap-xl-3 flex-wrap">
            <div>
              <div className="d-flex align-items-center gap-2">
                <FontAwesomeIcon icon={faChartLine} style={{ color: '#2563eb', fontSize: '1.15rem' }} />
                <h5 className="mb-0 fw-bold text-900" style={{ fontSize: '1.05rem' }}>
                  Progresión de Tickets por Hora
                </h5>
              </div>
              <p className="text-500 mb-0 mt-1" style={{ fontSize: '0.80rem' }}>
                Comportamiento acumulado de tickets recibidos, tomados por asesores y resueltos.
              </p>
            </div>

            {/* Badges compactos de estadísticas al lado del header */}
            <div className="d-flex align-items-center flex-wrap gap-2 pt-1 pt-xl-0">
              <div
                className="d-flex align-items-center gap-2 px-2 py-1 rounded-2 border"
                style={{
                  backgroundColor: isDark ? 'rgba(37, 99, 235, 0.12)' : '#eff6ff',
                  borderColor: isDark ? 'rgba(37, 99, 235, 0.25)' : '#dbeafe'
                }}
                title="Total acumulado de tickets llegados"
              >
                <div className="d-flex align-items-center gap-1" style={{ color: '#2563eb', fontSize: '0.74rem', fontWeight: 600 }}>
                  <FontAwesomeIcon icon={faInbox} /> Llegados:
                </div>
                <span className="fw-bold text-900" style={{ fontSize: '0.90rem' }}>
                  {totalLlegados}
                </span>
              </div>

              <div
                className="d-flex align-items-center gap-2 px-2 py-1 rounded-2 border"
                style={{
                  backgroundColor: isDark ? 'rgba(139, 92, 246, 0.12)' : '#f5f3ff',
                  borderColor: isDark ? 'rgba(139, 92, 246, 0.25)' : '#ede9fe'
                }}
                title="Total acumulado de tickets tomados por agentes"
              >
                <div className="d-flex align-items-center gap-1" style={{ color: '#8b5cf6', fontSize: '0.74rem', fontWeight: 600 }}>
                  <FontAwesomeIcon icon={faHandsHelping} /> Tomados:
                </div>
                <span className="fw-bold text-900" style={{ fontSize: '0.90rem' }}>
                  {totalTomados}
                </span>
              </div>

              <div
                className="d-flex align-items-center gap-2 px-2 py-1 rounded-2 border"
                style={{
                  backgroundColor: isDark ? 'rgba(22, 163, 74, 0.12)' : '#f0fdf4',
                  borderColor: isDark ? 'rgba(22, 163, 74, 0.25)' : '#dcfce7'
                }}
                title="Total acumulado de tickets resueltos"
              >
                <div className="d-flex align-items-center gap-1" style={{ color: '#16a34a', fontSize: '0.74rem', fontWeight: 600 }}>
                  <FontAwesomeIcon icon={faCheckCircle} /> Resueltos:
                </div>
                <span className="fw-bold text-900" style={{ fontSize: '0.90rem' }}>
                  {totalResueltos}
                </span>
              </div>

              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip id="tooltip-tasa-resolucion">
                    <div className="p-1" style={{ fontSize: '0.78rem' }}>
                      Porcentaje de tickets resueltos respecto al total de tickets que entraron en la jornada.
                    </div>
                  </Tooltip>
                }
              >
                <div
                  className="d-flex align-items-center gap-2 px-2 py-1 rounded-2 border"
                  style={{
                    backgroundColor: isDark ? 'rgba(2, 132, 199, 0.12)' : '#f0f9ff',
                    borderColor: isDark ? 'rgba(2, 132, 199, 0.25)' : '#e0f2fe',
                    cursor: 'help'
                  }}
                >
                  <div className="d-flex align-items-center gap-1" style={{ color: '#0284c7', fontSize: '0.74rem', fontWeight: 600 }}>
                    <FontAwesomeIcon icon={faPercent} /> Resolución:
                  </div>
                  <span className="fw-bold text-900" style={{ fontSize: '0.90rem' }}>
                    {tasaResolucion}%
                  </span>
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    style={{ fontSize: '0.70rem', color: '#0284c7', opacity: 0.8 }}
                  />
                </div>
              </OverlayTrigger>
            </div>
          </div>

          {/* Filtros: Selector de Asesor y Selector de Fecha */}
          <div className="d-flex flex-wrap align-items-center gap-2 w-100 w-lg-auto justify-content-between justify-content-lg-end">
            {onSelectAgent && (
              <div className="flex-grow-1 flex-sm-grow-0" style={{ minWidth: '140px', maxWidth: '100%' }}>
                <AgentFilterSelect
                  selectedAgentId={selectedAgentId}
                  onSelectAgent={onSelectAgent}
                  agents={agents}
                  currentUserId={effectiveUserId}
                  currentUserName={effectiveUserName}
                  isLoadingAgents={isLoadingAgents}
                />
              </div>
            )}

            {/* Selector de fecha con botones previo/siguiente */}
            <div className="d-flex align-items-center bg-light rounded-2 px-1 border" style={{ height: '36px' }}>
              <Button
                variant="link"
                size="sm"
                className="p-1 text-secondary"
                onClick={handlePrevDay}
                title="Día anterior"
              >
                <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: '0.75rem' }} />
              </Button>

              <div className="d-flex align-items-center px-1">
                <FontAwesomeIcon icon={faCalendarAlt} className="text-primary me-1" style={{ fontSize: '0.85rem' }} />
                <DatePicker
                  selected={selectedDateObj}
                  onChange={date => setSelectedDateObj(date || new Date())}
                  locale="es"
                  dateFormat="d 'de' MMMM, yyyy"
                  className="form-control form-control-sm border-0 bg-transparent text-center px-1"
                  style={{ minWidth: '150px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}
                />
              </div>

              <Button
                variant="link"
                size="sm"
                className="p-1 text-secondary"
                onClick={handleNextDay}
                title="Día siguiente"
              >
                <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: '0.75rem' }} />
              </Button>

              {!isToday && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="ms-1 py-0 px-2"
                  style={{ fontSize: '0.72rem', height: '24px' }}
                  onClick={handleGoToday}
                >
                  Hoy
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Gráfico */}
      <div className="card-body px-2 px-md-4 pt-0 pb-3" style={{ minHeight: '340px', position: 'relative' }}>
        {isLoading && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{
              backgroundColor: isDark ? 'rgba(30, 41, 59, 0.6)' : 'rgba(255, 255, 255, 0.6)',
              zIndex: 10,
              backdropFilter: 'blur(2px)',
              borderRadius: '8px'
            }}
          >
            <Spinner animation="border" variant="primary" />
          </div>
        )}

        <ReactEChartsCore
          echarts={echarts}
          option={getOption()}
          notMerge={true}
          lazyUpdate={true}
          style={{ height: '330px', width: '100%' }}
        />
      </div>
    </div>
  );
};

export default BurnupTicketsChart;
